import { create } from 'zustand';
import apiClient from '../service/apiClient';
import { toast } from 'react-toastify';
import { isAxiosError } from 'axios';

// 테마 타입
export interface Theme {
  id: string;
  name: string;
  icon: string;
  headerStyle: string;
  buttonStyle: string;
}

// 템플릿 설정 타입
export interface EmailTemplateConfig {
  franchiseId: string;
  franchiseName: string;
  title: string;
  subtitle: string;
  themeId: string;
  kakaoChannelUrl?: string;
  senderName?: string;
  isDefault: boolean;
}

// 가맹점 정보
export interface Franchise {
  id: string;
  name: string;
}

// Store 타입
interface EmailTemplateStore {
  // 상태
  config: EmailTemplateConfig | null;
  themes: Theme[];
  franchises: Franchise[];
  previewHtml: string;
  loading: boolean;
  isAdmin: boolean;
  selectedFranchiseId: string;

  // 액션
  loadTemplateConfig: (franchiseId: string) => Promise<void>;
  saveTemplateConfig: (config: Partial<EmailTemplateConfig>) => Promise<void>;
  resetTemplateConfig: () => Promise<void>;
  loadThemes: () => Promise<void>;
  loadFranchises: () => Promise<void>;
  generatePreview: (config: Partial<EmailTemplateConfig>) => Promise<void>;
  setSelectedFranchise: (franchiseId: string) => void;
  checkUserRole: () => Promise<void>;
}

export const useEmailTemplateStore = create<EmailTemplateStore>((set, get) => ({
  // 초기 상태
  config: null,
  themes: [],
  franchises: [],
  previewHtml: '',
  loading: false,
  isAdmin: false,
  selectedFranchiseId: '',

  // 템플릿 설정 불러오기
  loadTemplateConfig: async (franchiseId: string) => {
    set({ loading: true });
    try {
      const response = await apiClient.get(`/api/email-template/${franchiseId}`);
      set({ 
        config: response.data,
        selectedFranchiseId: franchiseId 
      });
      
      // 미리보기 생성
      await get().generatePreview(response.data);
    } catch (error) {
      if (isAxiosError(error)) {
        toast.error(error.response?.data?.message || '템플릿 불러오기 실패');
      }
    } finally {
      set({ loading: false });
    }
  },

  // 템플릿 설정 저장
  saveTemplateConfig: async (config: Partial<EmailTemplateConfig>) => {
    set({ loading: true });
    try {
      const { selectedFranchiseId } = get();
      const requestData = {
        franchiseId: selectedFranchiseId,
        title: config.title,
        subtitle: config.subtitle,
        themeId: config.themeId,
        kakaoChannelUrl: config.kakaoChannelUrl,
        senderName: config.senderName
      };

      const response = await apiClient.post('/api/email-template', requestData);
      set({ config: response.data });
      toast.success('템플릿이 저장되었습니다!');
      
      // 미리보기 갱신
      await get().generatePreview(response.data);
    } catch (error) {
      if (isAxiosError(error)) {
        const message = error.response?.data?.message || '템플릿 저장 실패';
        toast.error(message);
      }
    } finally {
      set({ loading: false });
    }
  },

  // 템플릿 초기화
  resetTemplateConfig: async () => {
    const confirmed = window.confirm('템플릿을 초기화하시겠습니까?\n기본값으로 돌아갑니다.');
    if (!confirmed) return;

    set({ loading: true });
    try {
      const { selectedFranchiseId } = get();
      await apiClient.delete(`/api/email-template/${selectedFranchiseId}`);
      
      // 기본값 다시 불러오기
      await get().loadTemplateConfig(selectedFranchiseId);
      toast.success('템플릿이 초기화되었습니다!');
    } catch (error) {
      if (isAxiosError(error)) {
        toast.error('템플릿 초기화 실패');
      }
    } finally {
      set({ loading: false });
    }
  },

  // 테마 목록 불러오기
  loadThemes: async () => {
    try {
      const response = await apiClient.get('/api/email-template/themes');
      set({ themes: response.data.themes });
    } catch (error) {
      console.error('테마 목록 불러오기 실패:', error);
    }
  },

  // 가맹점 목록 불러오기 (관리자용)
  loadFranchises: async () => {
    try {
      const response = await apiClient.get('/api/email-template/franchises');
      set({ franchises: response.data });
    } catch (error) {
      console.error('가맹점 목록 불러오기 실패:', error);
    }
  },

  // 미리보기 생성
  generatePreview: async (config: Partial<EmailTemplateConfig>) => {
    try {
      const { selectedFranchiseId } = get();
      const requestData = {
        franchiseId: selectedFranchiseId,
        title: config.title,
        subtitle: config.subtitle,
        themeId: config.themeId,
        kakaoChannelUrl: config.kakaoChannelUrl
      };

      const response = await apiClient.post('/api/email-template/preview', requestData);
      set({ previewHtml: response.data.html });
    } catch (error) {
      console.error('미리보기 생성 실패:', error);
    }
  },

  // 선택된 가맹점 변경
  setSelectedFranchise: (franchiseId: string) => {
    set({ selectedFranchiseId: franchiseId });
    get().loadTemplateConfig(franchiseId);
  },

  // 사용자 권한 확인
  checkUserRole: async () => {
    try {
      const response = await apiClient.get('/api/email-template/my-franchise');
      const { isAdmin, franchiseId } = response.data;
      
      set({ isAdmin });
      
      if (isAdmin) {
        // 관리자면 가맹점 목록 불러오기
        await get().loadFranchises();
      } else {
        // 일반 사용자면 자신의 가맹점 설정 불러오기
        set({ selectedFranchiseId: franchiseId });
        await get().loadTemplateConfig(franchiseId);
      }
    } catch (error) {
      console.error('권한 확인 실패:', error);
    }
  }
}))
